import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './FormRenderer.css';
import Select from 'react-select';

interface FormField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  default?: any;
  options?: Array<{ value: any; label: string } | string>;
}

interface SubmitAction {
  method: string;
  url: string;
  body: Record<string, any>;
  headers?: Record<string, string>;
  response: {
    status_handlers: Array<{
      status_code: number;
      show_toast?: boolean;
      toast_type?: string;
      message?: string;
      redirect_url?: string;
    }>;
    default_handler: {
      show_toast?: boolean;
      toast_type?: string;
      message?: string;
      redirect_url?: string;
    };
    clear_form?: boolean;
  };
}

interface FormBlock {
  title?: string;
  fields: FormField[];
  submit_action: SubmitAction;
}

interface FormRendererProps {
  block: FormBlock;
}

const FormRenderer: React.FC<FormRendererProps> = ({ block }) => {
  const navigate = useNavigate();
  const { getIdTokenClaims } = useAuth0();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Initialize form data with default values
  React.useEffect(() => {
    const defaults: Record<string, any> = {};
    block.fields.forEach(field => {
      defaults[field.key] = field.default || '';
    });
    setFormData(defaults);
  }, [block.fields]);

  const handleInputChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken.__raw;

      // Replace placeholders in body with actual values
      let requestBody = { ...block.submit_action.body };
      Object.keys(requestBody).forEach(key => {
        const value = requestBody[key];
        if (
          typeof value === 'string' &&
          value.startsWith('{') &&
          value.endsWith('}')
        ) {
          const fieldKey = value.slice(1, -1);
          requestBody[key] = formData[fieldKey];
        }
      });

      const response = await axios({
        method: block.submit_action.method,
        url: block.submit_action.url,
        data: requestBody,
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          ...block.submit_action.headers,
        },
      });

      // Handle response based on status handlers
      const statusHandler =
        block.submit_action.response.status_handlers.find(
          handler => handler.status_code === response.status
        ) || block.submit_action.response.default_handler;

      if (statusHandler.show_toast) {
        toast[statusHandler.toast_type](statusHandler.message);
      }

      if (statusHandler.redirect_url) {
        // Convert API URL to frontend route
        const redirectUrl = new URL(statusHandler.redirect_url);
        if (
          redirectUrl.pathname === '/v3/pages/dr1' ||
          redirectUrl.pathname === '/v3/pages/read/dr1'
        ) {
          navigate('/deal-rooms');
        }
      }

      if (block.submit_action.response.clear_form) {
        setFormData({});
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const defaultHandler = block.submit_action.response.default_handler;
      if (defaultHandler.show_toast) {
        toast[defaultHandler.toast_type](defaultHandler.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      required: field.required,
      placeholder: field.placeholder,
    };

    switch (field.type) {
      case 'text':
        return (
          <Form.Control
            type="text"
            value={formData[field.key] || field.default || ''}
            onChange={e => handleInputChange(field.key, e.target.value)}
            {...commonProps}
          />
        );

      case 'email':
        return (
          <Form.Control
            type="email"
            value={formData[field.key] || field.default || ''}
            onChange={e => handleInputChange(field.key, e.target.value)}
            {...commonProps}
          />
        );

      case 'long_text':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={formData[field.key] || field.default || ''}
            onChange={e => handleInputChange(field.key, e.target.value)}
            {...commonProps}
          />
        );

      case 'single_select':
        const selectOptions =
          field.options?.map(option => {
            if (typeof option === 'string') {
              return { value: option, label: option };
            }
            return { value: option.value, label: option.label };
          }) || [];

        return (
          <Select
            options={selectOptions}
            value={selectOptions.find(
              option => option.value === (formData[field.key] || field.default)
            )}
            onChange={selected => handleInputChange(field.key, selected?.value)}
            isClearable
            placeholder={field.placeholder}
            className="react-select-container"
            classNamePrefix="react-select"
            required={field.required}
          />
        );

      case 'multi_select':
        const multiSelectOptions =
          field.options?.map(option => {
            if (typeof option === 'string') {
              return { value: option, label: option };
            }
            return { value: option.value, label: option.label };
          }) || [];

        return (
          <Select
            isMulti
            options={multiSelectOptions}
            value={multiSelectOptions.filter(option =>
              (formData[field.key] || field.default || []).includes(
                option.value
              )
            )}
            onChange={selected =>
              handleInputChange(
                field.key,
                selected ? selected.map(item => item.value) : []
              )
            }
            placeholder={field.placeholder}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="form-container">
      {block.title && <h2>{block.title}</h2>}
      <Form onSubmit={handleSubmit}>
        {block.fields.map((field, index) => (
          <Form.Group key={index} className="mb-3">
            <Form.Label>{field.label}</Form.Label>
            {renderField(field)}
          </Form.Group>
        ))}
        <Button type="submit" disabled={isSubmitting} variant="primary">
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Form>
    </div>
  );
};

export default FormRenderer;
